import { useRef, useState } from "react";
import { api } from "../api";
import { Cell } from "../cells";
import type { BoardSnapshot, CellValue, Column, Group, Item } from "../types";

interface Props {
  snap: BoardSnapshot;
  reload: () => void;
  patchItemCell: (itemId: string, colId: string, value: CellValue) => void;
  updateCounts: Record<string, number>;
  onOpenItem: (item: Item) => void;
  isAdmin: boolean;
  canManage: boolean;
  query?: string;
  sort?: string;
  onlyMine?: boolean;
  currentUser?: { id: string; name: string };
}

const GROUP_COLORS = ["#ff158a", "#fdab3d", "#ffcb00", "#00c875", "#579bfc", "#66ccff", "#a25ddc", "#784bd1", "#e2445c", "#cab641", "#808080"];

// True if a people-type cell value references the given user (by id or name).
function cellHasUser(value: unknown, user: { id: string; name: string }): boolean {
  if (!value || typeof value !== "object") return false;
  const v = value as any;
  if (Array.isArray(v.users)) return v.users.some((u: any) => u.id === user.id || u.name === user.name);
  if (Array.isArray(v.names)) return v.names.some((n: string) => n === user.name);
  return false;
}

const COL_TYPES: Array<{ value: string; label: string }> = [
  { value: "text", label: "Text" },
  { value: "long_text", label: "Langer Text" },
  { value: "status", label: "Status" },
  { value: "people", label: "Personen" },
  { value: "date", label: "Datum" },
  { value: "timeline", label: "Timeline" },
  { value: "numbers", label: "Zahl" },
  { value: "money", label: "Budget (€)" },
  { value: "link", label: "Link" },
  { value: "files", label: "Dateien" },
];

// position helper: a value strictly between two siblings (fractional ordering).
function between(prev: number | undefined, next: number | undefined): number {
  if (prev == null && next == null) return 100;
  if (prev == null) return (next as number) - 50;
  if (next == null) return prev + 100;
  return (prev + next) / 2;
}

export function TableView({ snap, reload, patchItemCell, updateCounts, onOpenItem, isAdmin, canManage, query, sort, onlyMine, currentUser }: Props) {
  const { groups } = snap;
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [widths, setWidths] = useState<Record<string, number>>({});
  const [menuCol, setMenuCol] = useState<string | null>(null);
  const [addingCol, setAddingCol] = useState(false);
  const [newColTitle, setNewColTitle] = useState("");
  const [newColType, setNewColType] = useState("text");
  const [editGroup, setEditGroup] = useState<string | null>(null);
  const [colorGroup, setColorGroup] = useState<string | null>(null);

  const dragItem = useRef<{ id: string } | null>(null);
  const dragCol = useRef<string | null>(null);
  const [dropItem, setDropItem] = useState<string | null>(null);

  const visibleCols = snap.columns.filter((c) => !c.hidden).sort((a, b) => a.position - b.position);
  const hiddenCols = snap.columns.filter((c) => c.hidden);
  const colWidth = (c: Column) => widths[c.id] ?? c.width;
  const peopleCols = snap.columns.filter((c) => c.type === "people");

  // Items for a group, with search / "only mine" filtering and sorting applied.
  const byGroup = (g: Group) => {
    let rows = snap.items.filter((i) => i.group_id === g.id);
    const q = (query ?? "").trim().toLowerCase();
    if (q) rows = rows.filter((i) => i.name.toLowerCase().includes(q));
    if (onlyMine && currentUser)
      rows = rows.filter((i) => peopleCols.some((c) => cellHasUser(i.cells[c.id], currentUser)));
    switch (sort) {
      case "name": rows = [...rows].sort((a, b) => a.name.localeCompare(b.name)); break;
      case "name_desc": rows = [...rows].sort((a, b) => b.name.localeCompare(a.name)); break;
      case "updated": rows = [...rows].sort((a, b) => b.updated_at - a.updated_at); break;
      default: rows = [...rows].sort((a, b) => a.position - b.position);
    }
    return rows;
  };

  async function addItem(groupId: string) {
    await api.addItem(snap.board.id, { name: "Neues Projekt", groupId });
    reload();
  }

  // ───────── row drag & drop (reorder + move between groups) ─────────
  async function dropOnRow(target: Item) {
    const src = dragItem.current;
    dragItem.current = null; setDropItem(null);
    if (!src || src.id === target.id) return;
    const groupItems = byGroup(groups.find((g) => g.id === target.group_id)!).filter((i) => i.id !== src.id);
    const idx = groupItems.findIndex((i) => i.id === target.id);
    const pos = between(groupItems[idx - 1]?.position, target.position);
    await api.updateItem(src.id, { groupId: target.group_id, position: pos });
    reload();
  }
  async function dropOnGroup(groupId: string) {
    const src = dragItem.current;
    dragItem.current = null; setDropItem(null);
    if (!src) return;
    const last = byGroup(groups.find((g) => g.id === groupId)!).filter((i) => i.id !== src.id).at(-1);
    await api.updateItem(src.id, { groupId, position: (last?.position ?? 0) + 100 });
    reload();
  }

  // ───────── column drag (reorder) ─────────
  async function dropOnCol(target: Column) {
    const srcId = dragCol.current;
    dragCol.current = null;
    if (!srcId || srcId === target.id) return;
    const ordered = visibleCols.filter((c) => c.id !== srcId);
    const idx = ordered.findIndex((c) => c.id === target.id);
    const pos = between(ordered[idx - 1]?.position, target.position);
    await api.updateColumn(srcId, { position: pos });
    reload();
  }

  // ───────── column resize ─────────
  function startResize(col: Column, e: React.MouseEvent) {
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX;
    const startW = colWidth(col);
    const onMove = (ev: MouseEvent) => setWidths((w) => ({ ...w, [col.id]: Math.max(60, startW + ev.clientX - startX) }));
    const onUp = (ev: MouseEvent) => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      const final = Math.max(60, startW + ev.clientX - startX);
      api.updateColumn(col.id, { width: final }).catch(() => {});
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }

  async function hideCol(id: string) { setMenuCol(null); await api.updateColumn(id, { hidden: true }); reload(); }
  async function showCol(id: string) { await api.updateColumn(id, { hidden: false }); reload(); }
  async function toggleAdminOnly(c: Column) { setMenuCol(null); await api.updateColumn(c.id, { adminOnly: !c.admin_only }); reload(); }

  async function deleteColumn(c: Column) {
    setMenuCol(null);
    if (!confirm(`Spalte „${c.title}" und alle ihre Werte löschen?`)) return;
    await api.deleteColumn(c.id);
    reload();
  }

  async function addColumn() {
    const title = newColTitle.trim() || "Neue Spalte";
    // Give new status columns a usable default label set.
    const settings = newColType === "status"
      ? { labels: [
          { id: crypto.randomUUID(), label: "Offen", color: "#fdab3d" },
          { id: crypto.randomUUID(), label: "In Arbeit", color: "#579bfc" },
          { id: crypto.randomUUID(), label: "Erledigt", color: "#00c875" },
        ] }
      : newColType === "money" ? { unit: "€" } : {};
    await api.addColumn(snap.board.id, { title, type: newColType, settings });
    setAddingCol(false); setNewColTitle(""); setNewColType("text");
    reload();
  }

  // ───────── group + item management ─────────
  async function renameGroup(id: string, title: string) {
    setEditGroup(null);
    if (!title.trim()) return;
    await api.updateGroup(id, { title: title.trim() });
    reload();
  }
  async function recolorGroup(id: string, color: string) {
    setColorGroup(null);
    await api.updateGroup(id, { color });
    reload();
  }
  async function removeGroup(g: Group) {
    if (!confirm(`Gruppe „${g.title}" und alle enthaltenen Projekte löschen?`)) return;
    await api.deleteGroup(g.id);
    reload();
  }
  async function removeItem(it: Item) {
    if (!confirm(`Projekt „${it.name}" löschen?`)) return;
    await api.deleteItem(it.id);
    reload();
  }

  return (
    <>
      {hiddenCols.length > 0 && (
        <div className="hidden-cols-bar">
          <span className="muted" style={{ fontSize: 11 }}>Ausgeblendet:</span>
          {hiddenCols.map((c) => (
            <span key={c.id} className="hidden-chip" onClick={() => showCol(c.id)} title="Wieder einblenden">+ {c.title}</span>
          ))}
        </div>
      )}
      <div className="board-scroll">
        {groups.map((g) => {
          const rows = byGroup(g);
          const isOpen = !collapsed[g.id];
          return (
            <div className="group-block" key={g.id}>
              <div className="group-head" onDragOver={(e) => e.preventDefault()} onDrop={() => dropOnGroup(g.id)}>
                <button className="group-caret" onClick={() => setCollapsed((c) => ({ ...c, [g.id]: isOpen }))}>{isOpen ? "▼" : "▶"}</button>
                {canManage && (
                  <span style={{ position: "relative" }}>
                    <button className="group-caret" title="Farbe ändern"
                      style={{ width: 12, height: 12, borderRadius: 3, background: g.color, padding: 0 }}
                      onClick={() => setColorGroup(colorGroup === g.id ? null : g.id)} />
                    {colorGroup === g.id && (
                      <div className="col-dropdown" style={{ display: "flex", flexWrap: "wrap", gap: 4, minWidth: 0, width: 140 }}
                        onMouseLeave={() => setColorGroup(null)}>
                        {GROUP_COLORS.map((col) => (
                          <span key={col} onClick={() => recolorGroup(g.id, col)}
                            style={{ width: 22, height: 22, borderRadius: 4, background: col, cursor: "pointer" }} />
                        ))}
                      </div>
                    )}
                  </span>
                )}
                {editGroup === g.id ? (
                  <input className="input" autoFocus defaultValue={g.title} style={{ width: 220, padding: "3px 8px" }}
                    onBlur={(e) => renameGroup(g.id, e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && renameGroup(g.id, (e.target as HTMLInputElement).value)} />
                ) : (
                  <span className="group-title" style={{ color: g.color, cursor: canManage ? "text" : "default" }}
                    onDoubleClick={() => canManage && setEditGroup(g.id)} title={canManage ? "Doppelklick zum Umbenennen" : undefined}>
                    {g.title}
                  </span>
                )}
                <span className="group-count">{rows.length} Projekte</span>
                {canManage && (
                  <button className="group-caret" title="Gruppe löschen" style={{ color: "var(--text-faint)" }}
                    onClick={() => removeGroup(g)}>🗑</button>
                )}
              </div>

              {isOpen && (
                <table className="tbl">
                  <thead>
                    <tr>
                      <th className="colorbar" style={{ background: g.color }} />
                      <th style={{ width: 30 }} />
                      <th className="name-cell">Projekt</th>
                      {visibleCols.map((c) => (
                        <th key={c.id} style={{ minWidth: colWidth(c), width: colWidth(c) }}
                          draggable onDragStart={() => (dragCol.current = c.id)}
                          onDragOver={(e) => e.preventDefault()} onDrop={() => dropOnCol(c)}>
                          <div className="col-head-inner">
                            <span style={{ flex: 1 }}>{c.admin_only ? "🔒 " : ""}{c.title}</span>
                            <span className="col-menu-btn" onClick={(e) => { e.stopPropagation(); setMenuCol(menuCol === c.id ? null : c.id); }}>⋯</span>
                          </div>
                          {menuCol === c.id && (
                            <div className="col-dropdown" onMouseLeave={() => setMenuCol(null)}>
                              <div onClick={() => hideCol(c.id)}>👁 Ausblenden</div>
                              {isAdmin && (
                                <div onClick={() => toggleAdminOnly(c)}>
                                  {c.admin_only ? "🔓 Für alle sichtbar" : "🔒 Nur für Admins"}
                                </div>
                              )}
                              {canManage && (
                                <div style={{ color: "var(--danger)" }} onClick={() => deleteColumn(c)}>🗑 Spalte löschen</div>
                              )}
                            </div>
                          )}
                          <div className="col-resize" onMouseDown={(e) => startResize(c, e)} />
                        </th>
                      ))}
                      {canManage && (
                        <th style={{ width: 120, position: "relative" }}>
                          <span className="add-col-btn" onClick={() => setAddingCol((v) => !v)} title="Spalte hinzufügen">＋ Spalte</span>
                          {addingCol && (
                            <div className="col-dropdown" style={{ left: "auto", right: 0, minWidth: 200 }}>
                              <input className="input" autoFocus placeholder="Spaltenname" value={newColTitle}
                                onChange={(e) => setNewColTitle(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && addColumn()}
                                style={{ marginBottom: 6, padding: "6px 8px" }} />
                              <select className="input" value={newColType} onChange={(e) => setNewColType(e.target.value)}
                                style={{ marginBottom: 8, padding: "6px 8px" }}>
                                {COL_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                              </select>
                              <div className="row-flex" style={{ justifyContent: "flex-end", gap: 6 }}>
                                <button className="btn btn-ghost btn-sm" onClick={() => setAddingCol(false)}>Abbrechen</button>
                                <button className="btn btn-sm" onClick={addColumn}>Hinzufügen</button>
                              </div>
                            </div>
                          )}
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((it) => (
                      <tr key={it.id}
                        className={dropItem === it.id ? "drop-target" : ""}
                        onDragOver={(e) => { e.preventDefault(); setDropItem(it.id); }}
                        onDragLeave={() => setDropItem((d) => (d === it.id ? null : d))}
                        onDrop={() => dropOnRow(it)}>
                        <td className="colorbar" style={{ background: g.color }} />
                        <td style={{ width: 30, textAlign: "center" }}>
                          <span className="row-grip" draggable
                            onDragStart={() => (dragItem.current = { id: it.id })}>⠿</span>
                        </td>
                        <td className="name-cell">
                          <div className="cell" style={{ gap: 8 }}>
                            <span className="expand-btn" onClick={() => onOpenItem(it)} title="Updates öffnen">
                              💬
                              {updateCounts[it.id] ? <span className="update-badge">{updateCounts[it.id]}</span> : null}
                            </span>
                            <NameInput item={it} />
                            {canManage && (
                              <span className="row-grip" title="Projekt löschen"
                                style={{ marginLeft: "auto", cursor: "pointer" }}
                                onClick={() => removeItem(it)}>🗑</span>
                            )}
                          </div>
                        </td>
                        {visibleCols.map((c) => (
                          <td key={c.id} style={{ minWidth: colWidth(c), width: colWidth(c) }}>
                            <Cell item={it} col={c} onChange={(v) => patchItemCell(it.id, c.id, v)} />
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr>
                      <td className="colorbar" style={{ background: g.color, opacity: .4 }} />
                      <td className="add-row" colSpan={visibleCols.length + 2} onClick={() => addItem(g.id)}>+ Projekt hinzufügen</td>
                    </tr>
                    <GroupSummary rows={rows} columns={visibleCols} />
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

function NameInput({ item }: { item: Item }) {
  const [name, setName] = useState(item.name);
  return (
    <input className="cell-input" value={name} style={{ fontWeight: 600 }}
      onChange={(e) => setName(e.target.value)}
      onBlur={() => name !== item.name && api.updateItem(item.id, { name }).catch(() => {})} />
  );
}

function GroupSummary({ rows, columns }: { rows: Item[]; columns: Column[] }) {
  return (
    <tr className="group-summary">
      <td className="colorbar" /><td style={{ width: 30 }} /><td />
      {columns.map((c) => {
        if (c.type === "money" || c.type === "numbers") {
          const sum = rows.reduce((acc, it) => {
            const v = it.cells[c.id];
            return acc + (v && typeof v === "object" && "number" in v ? v.number : 0);
          }, 0);
          return <td key={c.id}><div className="cell money">{sum ? `${sum.toLocaleString("de-DE")} ${c.settings.unit || "€"}` : ""}</div></td>;
        }
        if (c.type === "files") {
          const n = rows.reduce((acc, it) => {
            const v = it.cells[c.id];
            return acc + (v && typeof v === "object" && "files" in v ? (v.files as unknown[]).length : 0);
          }, 0);
          return <td key={c.id}><div className="cell muted">{n ? `${n} Dateien` : ""}</div></td>;
        }
        return <td key={c.id} />;
      })}
    </tr>
  );
}
